import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AtSign, ArrowUpRight, ExternalLink, Train, Bus, MapPin, Bike } from 'lucide-react';
import CampaignSlot from './CampaignSlot';
import WhatsAppIcon from './icons/WhatsAppIcon';
import TrainFluoBadge from './TrainFluoBadge';

const FacebookIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M22 12.06C22 6.51 17.52 2 12 2S2 6.51 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.84c0-2.52 1.49-3.91 3.78-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.44 2.91h-2.34V22c4.78-.76 8.43-4.92 8.43-9.94z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.22.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.05.41 2.22.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.22-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.05.36-2.22.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.22-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.05-.41-2.22C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.22.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.05-.36 2.22-.41C8.42 2.21 8.8 2.2 12 2.2zm0 2c-3.15 0-3.5.01-4.74.07-1 .04-1.55.21-1.92.35-.48.19-.83.41-1.19.77-.36.36-.58.71-.77 1.19-.14.37-.31.92-.35 1.92C2.97 8.5 2.96 8.85 2.96 12s.01 3.5.07 4.74c.04 1 .21 1.55.35 1.92.19.48.41.83.77 1.19.36.36.71.58 1.19.77.37.14.92.31 1.92.35 1.24.06 1.59.07 4.74.07s3.5-.01 4.74-.07c1-.04 1.55-.21 1.92-.35.48-.19.83-.41 1.19-.77.36-.36.58-.71.77-1.19.14-.37.31-.92.35-1.92.06-1.24.07-1.59.07-4.74s-.01-3.5-.07-4.74c-.04-1-.21-1.55-.35-1.92a3.2 3.2 0 0 0-.77-1.19 3.2 3.2 0 0 0-1.19-.77c-.37-.14-.92-.31-1.92-.35C15.5 4.21 15.15 4.2 12 4.2zm0 3.4a4.4 4.4 0 1 1 0 8.8 4.4 4.4 0 0 1 0-8.8zm0 2a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8zm5.05-1.85a1.05 1.05 0 1 1-2.1 0 1.05 1.05 0 0 1 2.1 0z"/>
  </svg>
);

const ICON_MAP = {
  atsign:    AtSign,
  whatsapp:  WhatsAppIcon,
  facebook:  FacebookIcon,
  instagram: InstagramIcon,
};

const PALETTE = {
  blue:      { bg: 'bg-ter-blue/10',  text: 'text-ter-blue',  hoverBd: 'hover:border-ter-blue' },
  facebook:  { bg: 'bg-[#1877F2]/10', text: 'text-[#1877F2]', hoverBd: 'hover:border-[#1877F2]' },
  whatsapp:  { bg: 'bg-[#25D366]/10', text: 'text-[#25D366]', hoverBd: 'hover:border-[#25D366]' },
  instagram: { bg: 'bg-gradient-to-tr from-[#feda75] via-[#fa7e1e] to-[#d62976]', text: 'text-white', hoverBd: 'hover:border-[#d62976]' },
};

const InfoLink = ({ href, icon: Icon, label, sublabel, accent = 'blue', testId, target = '_blank' }) => {
  const palette = PALETTE[accent] ?? PALETTE.blue;
  return (
    <a
      href={href}
      target={target}
      rel="noopener noreferrer"
      data-testid={testId}
      className={`group flex items-center gap-4 p-4 rounded-xl border border-slate-200 bg-white transition-all hover:-translate-y-0.5 hover:shadow-md min-w-0 w-full ${palette.hoverBd}`}
    >
      <div className={`w-11 h-11 rounded-lg ${palette.bg} flex items-center justify-center shrink-0`}>
        <Icon className={`w-5 h-5 ${palette.text}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900 leading-snug">{label}</div>
        {sublabel && <div className="text-xs text-slate-500 truncate">{sublabel}</div>}
      </div>
      <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-ter-blue transition-colors shrink-0" />
    </a>
  );
};

const Stat = ({ icon: Icon, value, label, sublabel, prefix }) => (
  <div className="bg-white/5 rounded-lg px-3 py-2.5 border border-white/10">
    {prefix && <div className="text-[11px] text-white/80 leading-snug">{prefix}</div>}
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-ter-yellow" />
      <span className="text-xl font-extrabold tabular-nums">{value}</span>
    </div>
    <div className="text-[11px] text-white/80 leading-snug mt-0.5">{label}</div>
    {sublabel && <div className="text-[10px] text-white/60 leading-snug">{sublabel}</div>}
  </div>
);

const QUOTA_FALLBACK = { trains: '1 900', cars: '3 200', gares: '385', velos: '500' };

const MobilityHighlight = () => {
  const { t } = useTranslation();
  const [quota, setQuota] = useState(QUOTA_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    fetch(`/infofluo/quota.json?v=${Date.now()}`)
      .then(r => { if (!r.ok) throw r; return r.json(); })
      .then(data => { if (!cancelled) setQuota({ ...QUOTA_FALLBACK, ...data }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="mt-8 rounded-2xl border border-ter-yellow/40 text-white px-5 py-5 sm:px-6 sm:py-6 shadow-sm"
      style={{ background: 'linear-gradient(135deg, #191998 0%, #0D0D7A 100%)' }}
      data-testid="mobility-highlight"
    >
      <h3 className="text-lg sm:text-xl font-extrabold leading-tight">{t('mobility.title')}</h3>
      <p className="text-sm text-white/85 mt-2 max-w-2xl">{t('mobility.text')}</p>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={Train} value={quota.trains} label={t('mobility.stat_trains')} prefix={t('mobility.stat_trains_prefix')} />
        <Stat icon={Bus} value={quota.cars} label={t('mobility.stat_buses')} />
        <Stat icon={MapPin} value={quota.gares} label={t('mobility.stat_stations')} />
        <Stat icon={Bike} value={quota.velos} label={t('mobility.stat_bikes')} sublabel={t('mobility.stat_bikes_sub')} />
      </div>
      <a
        href="https://www.fluo.grandest.fr/actualites/le-reseau-fluo/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="mobility-more-info"
        className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-ter-yellow text-ter-blue font-bold hover:brightness-95 transition"
      >
        {t('mobility.more')} <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
};

const InfoFluo = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'fr').slice(0, 2);

  const [data, setData]       = useState(null);
  const [texts, setTexts]     = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`/infofluo/info_data.json?v=${Date.now()}`).then(r => { if (!r.ok) throw new Error(r.status); return r.json(); }),
      fetch(`/infofluo/info_langues.json?v=${Date.now()}`).then(r => { if (!r.ok) throw new Error(r.status); return r.json(); }),
    ])
      .then(([d, l]) => { setData(d); setTexts(l); })
      .catch(() => setLoadError(true));
  }, []);

  // Resolve translation key from loaded JSON, fallback to FR then empty string
  const tx = (key) => texts?.[lang]?.[key] ?? texts?.['fr']?.[key] ?? '';

  if (loadError) {
    return (
      <section id="info-fluo" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 scroll-mt-20" data-testid="info-fluo-section">
        <p className="text-sm text-slate-400 text-center py-8">Contenu temporairement indisponible.</p>
      </section>
    );
  }

  if (!data || !texts) {
    return (
      <section id="info-fluo" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 scroll-mt-20" data-testid="info-fluo-section">
        <div className="animate-pulse space-y-4">
          <div className="h-3 bg-slate-200 rounded w-28" />
          <div className="h-8 bg-slate-200 rounded w-72" />
          <div className="h-3 bg-slate-200 rounded w-56 mt-2" />
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-200 rounded-xl" />)}
          </div>
        </div>
      </section>
    );
  }

  const canaux = data.filter(e => e.theme === 'canal');
  const equipe = data.filter(e => e.theme === 'equipe');
  const plan   = data.filter(e => e.theme === 'plan');

  const renderCard = (entry) => {
    const Icon = ICON_MAP[entry.icon] ?? AtSign;
    return (
      <InfoLink
        key={entry.id}
        href={entry.url}
        icon={Icon}
        label={tx(entry.title_key)}
        sublabel={tx(entry.subtitle_key)}
        accent={entry.accent}
        target={entry.target}
        testId={entry.testId}
      />
    );
  };

  return (
    <section id="info-fluo" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 scroll-mt-20 overflow-x-hidden" data-testid="info-fluo-section">
      <div className="grid xl:grid-cols-3 gap-6 items-start">
        <div className="xl:col-span-2">
          <div className="text-xs uppercase tracking-wider text-ter-red font-bold mb-2">{tx('eyebrow')}</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-ter-blue leading-tight">
            {tx('title')} <TrainFluoBadge size="lg" label={tx('badge_plural')} className="align-middle" /> {tx('title_suffix')}
          </h2>
          <p className="text-slate-600 mt-2 text-sm">{tx('subtitle')}</p>

          {/* 1. Canaux officiels */}
          <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="info-fluo-grid">
            {canaux.map(renderCard)}
          </div>

          {/* 2. Équipe contact */}
          <p className="text-slate-600 mt-6 text-sm" data-testid="info-fluo-team-subtitle">
            {tx('team_subtitle')}
          </p>
          <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="info-fluo-contact-grid">
            {equipe.map(renderCard)}
          </div>

          {/* 3. Lifestyle / réseaux sociaux */}
          <p className="text-slate-600 mt-6 text-sm" data-testid="info-fluo-lifestyle-subtitle">
            {tx('lifestyle_subtitle')}
          </p>
          <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="info-fluo-lifestyle-grid">
            {plan.map(renderCard)}
          </div>

          <MobilityHighlight />
        </div>

        <aside>
          <div className="text-xs tracking-wider text-slate-500 font-semibold mb-3">{t('campaign.sidebar_title')}</div>
          <CampaignSlot />
        </aside>
      </div>
    </section>
  );
};

export default InfoFluo;
